import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Search, Plus, Building2, Phone, Mail, MapPin, TrendingUp, TrendingDown,
  FileText, Receipt, Wallet, StickyNote, Edit, UserPlus, Calendar,
  Clock, CheckCircle2, AlertCircle, Hash, Users, DollarSign, Award, Monitor, MoreVertical, Trash2,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  AreaChart, Area, XAxis, Tooltip as ReTooltip, ResponsiveContainer,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0) + ' FCFA';
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.0', '') + ' M';
  if (v >= 1_000) return (v / 1_000).toFixed(0) + ' k';
  return String(v);
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return '—'; }
};
const initials = (s = '') => s.trim().split(/\s+/).slice(0, 1).map(w => w[0]?.toUpperCase() || '').join('') || '•';
const PALETTE_AVATAR = ['bg-orange-500', 'bg-purple-600', 'bg-emerald-500', 'bg-rose-500', 'bg-blue-500', 'bg-amber-500'];
const avatarColor = (nom = '') => PALETTE_AVATAR[(nom.charCodeAt(0) || 0) % PALETTE_AVATAR.length];

const RENEWAL_COLORS = {
  Acquis:    { fg: 'text-emerald-700',  dot: 'bg-emerald-500',  gradient: 'from-emerald-500 to-emerald-700' },
  Probable:  { fg: 'text-emerald-600',  dot: 'bg-emerald-400',  gradient: 'from-amber-400 via-lime-500 to-emerald-500' },
  Incertain: { fg: 'text-amber-600',    dot: 'bg-amber-400',    gradient: 'from-rose-400 via-amber-400 to-emerald-500' },
  Faible:    { fg: 'text-rose-600',     dot: 'bg-rose-500',     gradient: 'from-rose-500 to-amber-500' },
};

export default function Clients({ user, onLogout }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('locations');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/clients/with-renewal`);
      setClients(r.data || []);
      if ((r.data || []).length > 0 && !selectedId) {
        setSelectedId(r.data[0].id);
      }
    } catch (e) {
      toast.error('Erreur chargement clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteClient = async (c) => {
    if (!window.confirm(`Supprimer le client « ${c.entreprise} » ?\nIl restera dans vos prospects.`)) return;
    try {
      await axios.delete(`${API}/api/clients/${c.id}`);
      toast.success('Client supprimé');
      setClients((list) => list.filter((x) => x.id !== c.id));
      if (selectedId === c.id) { setSelectedId(null); setDetails(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  };

  useEffect(() => {
    if (!selectedId) return;
    setLoadingDetails(true);
    axios.get(`${API}/api/clients/${selectedId}/renewal-details`)
      .then(r => setDetails(r.data))
      .catch(() => toast.error('Erreur chargement détails'))
      .finally(() => setLoadingDetails(false));
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      (c.entreprise || '').toLowerCase().includes(q) ||
      (c.contact || '').toLowerCase().includes(q) ||
      (c.secteur || '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  // KPIs du bandeau haut (clients actifs, panneaux loués total, CA cumulé)
  const headerKpis = useMemo(() => {
    return {
      actifs: clients.length,
      panneaux: clients.reduce((s, c) => s + 0, 0), // on complète via details
      ca: 0, // calculé via details.kpis.ca_total si disponible
    };
  }, [clients]);

  const totalPanneaux = details?.kpis?.panneaux_loues || 0;
  const totalCA       = details?.kpis?.ca_total || 0;

  return (
    <Layout user={user} onLogout={onLogout} currentPage="clients">
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5" data-testid="clients-root">
        {/* ═══════════════════ COLONNE GAUCHE : liste des clients ═══════════════════ */}
        <aside className="space-y-3">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900">Clients</h1>
            <p className="text-sm text-slate-500">Gérez et suivez vos clients et leurs informations.</p>
          </div>

          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Rechercher un client, une entreprise…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-white"
              data-testid="input-search-clients"
            />
          </div>

          {/* Mini KPIs */}
          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Clients actifs"   value={clients.length} testid="stat-clients-actifs" />
            <MiniStat label="Panneaux loués"   value={totalPanneaux}  testid="stat-panneaux-loues" />
            <MiniStat label="FCFA cumulés"     value={fmtShort(totalCA)} testid="stat-ca-cumule" />
          </div>

          {/* Liste */}
          {loading ? (
            <div className="flex items-center justify-center h-72"><div className="animate-spin rounded-full h-10 w-10 border-b-4 border-blue-600" /></div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-sm text-slate-500">Aucun client</CardContent>
            </Card>
          ) : (
            <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
              {filtered.map((c) => {
                const sel = c.id === selectedId;
                const rl = RENEWAL_COLORS[c.renewal_label] || RENEWAL_COLORS.Incertain;
                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full text-left cursor-pointer rounded-xl border bg-white px-3 py-3 transition shadow-sm hover:shadow ${
                      sel ? 'border-blue-600 ring-2 ring-blue-100' : 'border-slate-200'
                    }`}
                    data-testid={`client-card-${c.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-md ${avatarColor(c.entreprise)} text-white font-bold flex items-center justify-center flex-shrink-0`}>
                        {initials(c.entreprise)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 text-sm truncate leading-tight">{c.entreprise}</p>
                        <p className="text-[11px] text-slate-500 truncate">{c.secteur || '—'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-sm font-extrabold ${rl.fg}`}>{c.renewal_score}%</p>
                        <p className="text-[10px] text-slate-400 leading-tight">renouvel.</p>
                      </div>
                      {user?.role === 'admin' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button onClick={(e) => e.stopPropagation()} className="p-1 -mr-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-testid={`client-menu-${c.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem className="text-rose-600 focus:text-rose-700 gap-2" onClick={() => deleteClient(c)} data-testid={`client-delete-${c.id}`}>
                              <Trash2 className="h-4 w-4" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    <div className="w-full h-1 bg-slate-100 rounded-full mt-2 overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${rl.gradient}`} style={{ width: `${c.renewal_score}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* ═══════════════════ COLONNE DROITE : détails client ═══════════════════ */}
        <main>
          {loadingDetails ? (
            <div className="h-72 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600" /></div>
          ) : !details ? (
            <Card>
              <CardContent className="p-10 text-center text-sm text-slate-500">
                Sélectionnez un client à gauche pour voir sa fiche.
              </CardContent>
            </Card>
          ) : (
            <ClientDetails
              d={details}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onRefresh={() => { load(); if (selectedId) { axios.get(`${API}/api/clients/${selectedId}/renewal-details`).then(r => setDetails(r.data)); } }}
            />
          )}
        </main>
      </div>
    </Layout>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-2.5 text-center" data-testid={testid}>
      <p className="text-xl font-extrabold text-slate-900 leading-none">{value}</p>
      <p className="text-[10px] text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function ClientDetails({ d, activeTab, setActiveTab, onRefresh }) {
  const c = d.client;
  const rl = RENEWAL_COLORS[d.renewal.label] || RENEWAL_COLORS.Incertain;

  // Position du marqueur sur la barre de renouvellement (gradient 4 zones)
  // 0-35% Faible | 35-60% Incertain | 60-85% Probable | 85-100% Acquis
  const renewalPct = d.renewal.score;

  // Barre d'ancienneté : max 5 ans = 60 mois
  const ancienneteSteps = 5;
  const monthsPerStep = 12;
  const filledSteps = Math.min(ancienneteSteps, Math.ceil(d.anciennete.months / monthsPerStep));
  const premiereRelance = d.relances?.[0];

  return (
    <div className="space-y-4">
      {/* ─────── En-tête client ─────── */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className={`w-14 h-14 rounded-xl ${avatarColor(c.entreprise)} text-white font-bold text-2xl flex items-center justify-center flex-shrink-0`}>
                {initials(c.entreprise)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-2xl font-extrabold text-slate-900 leading-tight">{c.entreprise}</h2>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">Actif</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                  {c.secteur && <span>{c.secteur}</span>}
                  {c.contact && <><span className="text-slate-300">·</span><span>{c.contact}</span></>}
                  {c.telephone && <><span className="text-slate-300">·</span><a href={`tel:${c.telephone}`} className="hover:text-blue-600">{c.telephone}</a></>}
                </p>
                {c.email && (
                  <p className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
                    <Mail className="h-3 w-3" /> <a href={`mailto:${c.email}`} className="hover:text-blue-600">{c.email}</a>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="gap-1"><Edit className="h-3.5 w-3.5" /> Modifier</Button>
              <Button variant="outline" size="sm" className="gap-1"><Receipt className="h-3.5 w-3.5" /> Remplir une facture</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="btn-new-location">
                <Plus className="h-3.5 w-3.5" /> Nouvelle location
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─────── Tableaux d'évolution (type Suivi) + Ancienneté ─────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Tableaux d'évolution — 2 colonnes */}
        <div className="lg:col-span-2">
          <ClientEvolutionTables d={d} />
        </div>

        {/* Ancienneté */}
        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-2">Ancienneté</h3>
            <p className="text-3xl font-black text-slate-900 leading-tight">
              {d.anciennete.label}
              <span className="text-sm font-normal text-slate-500 ml-2">depuis {d.anciennete.depuis || '—'}</span>
            </p>
            <div className="flex gap-1 mt-4">
              {Array.from({ length: ancienneteSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 flex-1 rounded-full ${i < filledSteps ? 'bg-blue-600' : 'bg-slate-200'}`}
                />
              ))}
            </div>
            <p className="text-xs text-slate-600 mt-3">
              Fidélité : <span className="font-bold text-slate-900">{d.fidelite}</span>
              {d.contrats.length > 1 && <span className="text-slate-500"> — {d.contrats.length - 1} renouvellements</span>}
            </p>

            {/* Probabilité renouvellement intégrée en bas (compacte) */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-slate-700">Probabilité de renouvellement</h4>
                <span className={`text-xs font-extrabold ${rl.fg}`}>{renewalPct}% {d.renewal.label}</span>
              </div>
              <div className="relative w-full h-2 rounded-full overflow-hidden bg-gradient-to-r from-rose-400 via-amber-400 via-lime-400 to-emerald-500">
                <span
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 border-slate-700 shadow"
                  style={{ left: `calc(${renewalPct}% - 6px)` }}
                />
              </div>
              {premiereRelance && (
                <p className="text-[11px] text-rose-600 mt-2">Expire dans {premiereRelance.jours_restants} j · relance conseillée</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─────── 4 KPIs ─────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiBox label="Chiffre d'affaires cumulé" value={fmtEUR(d.kpis.ca_total)} />
        <KpiBox label="Contrats actifs"           value={d.kpis.contrats_actifs} />
        <KpiBox label="Panneaux loués"            value={d.kpis.panneaux_loues} />
        <KpiBox label="Paiements à jour"          value={`${d.kpis.pct_paiements_jour} %`} accent="emerald" />
      </div>

      {/* ─────── Tabs ─────── */}
      <Card className="rounded-2xl">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
            <div>
              <div className="flex items-center gap-5 border-b border-slate-200 mb-4 overflow-x-auto">
                {[
                  { k: 'locations',   label: 'Locations de panneaux' },
                  { k: 'autres',      label: 'Autres prestations' },
                  { k: 'paiements',   label: 'Paiements' },
                  { k: 'contrats',    label: 'Contrats' },
                  { k: 'notes',       label: 'Notes' },
                ].map(t => (
                  <button
                    key={t.k}
                    onClick={() => setActiveTab(t.k)}
                    className={`pb-3 text-sm font-semibold transition border-b-2 whitespace-nowrap ${
                      activeTab === t.k ? 'text-blue-600 border-blue-600' : 'text-slate-500 border-transparent hover:text-slate-700'
                    }`}
                    data-testid={`tab-${t.k}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {activeTab === 'locations' && (
                <TableLocations rows={d.locations_panneaux} />
              )}
              {activeTab === 'autres' && (
                <TableAutres rows={d.autres_prestations} />
              )}
              {activeTab === 'paiements' && (
                <TablePaiements rows={d.paiements} />
              )}
              {activeTab === 'contrats' && (
                <TableContrats rows={d.contrats} />
              )}
              {activeTab === 'notes' && (
                <div className="text-sm text-slate-500 italic">
                  {d.client.notes || 'Aucune note enregistrée pour ce client.'}
                </div>
              )}
            </div>

            {/* Historique */}
            <aside className="border-l border-slate-200 pl-5">
              <h4 className="text-sm font-bold text-slate-900 mb-3">Historique</h4>
              <ol className="space-y-3">
                {d.historique.slice(0, 8).map((ev, i) => (
                  <li key={i} className="flex gap-2 items-start text-xs">
                    <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      ev.type === 'paiement' ? 'bg-emerald-500' : ev.type === 'contrat' ? 'bg-blue-500' : 'bg-amber-500'
                    }`}></span>
                    <div className="min-w-0">
                      <p className="text-slate-800 font-semibold truncate">{ev.titre}</p>
                      <p className="text-slate-500">{new Date(ev.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} · {ev.detail}</p>
                    </div>
                  </li>
                ))}
                {d.historique.length === 0 && (
                  <li className="text-slate-400 text-xs italic">Aucun événement</li>
                )}
              </ol>
            </aside>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiBox({ label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-xl font-extrabold ${accent === 'emerald' ? 'text-emerald-600' : 'text-slate-900'} leading-tight`}>{value}</p>
    </div>
  );
}

function TableLocations({ rows }) {
  if (!rows?.length) return <EmptyMsg icon={Monitor} text="Aucune location de panneau pour ce client" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
            <th className="py-2">Panneau</th>
            <th className="py-2">Emplacement</th>
            <th className="py-2">Format</th>
            <th className="py-2">Période</th>
            <th className="py-2 text-right">Montant</th>
            <th className="py-2">Statut</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2.5 font-bold text-slate-800">{r.code}</td>
              <td className="py-2.5 text-slate-700">{r.emplacement || '—'}</td>
              <td className="py-2.5 text-slate-600">{r.format || '—'}</td>
              <td className="py-2.5 text-slate-700 text-xs">{fmtDate(r.date_debut)} → {fmtDate(r.date_fin)}</td>
              <td className="py-2.5 text-right font-extrabold">{fmtEUR(r.montant)}</td>
              <td className="py-2.5">
                <StatusBadge statut={r.statut} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableAutres({ rows }) {
  if (!rows?.length) return <EmptyMsg icon={FileText} text="Aucune autre prestation" />;
  return (
    <div className="space-y-2">
      <h4 className="font-bold text-slate-900 text-sm mt-2 mb-3">Autres prestations</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2">Désignation</th>
              <th className="py-2">Date</th>
              <th className="py-2 text-right">Montant</th>
              <th className="py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2.5 text-slate-800">{p.designation}</td>
                <td className="py-2.5 text-slate-700">{fmtDate(p.date)}</td>
                <td className="py-2.5 text-right font-bold">{fmtEUR(p.montant)}</td>
                <td className="py-2.5"><StatusBadge statut={p.statut} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TablePaiements({ rows }) {
  if (!rows?.length) return <EmptyMsg icon={Wallet} text="Aucun paiement enregistré" />;
  const modeLabel = { cheque: 'Chèque', virement: 'Virement', especes: 'Espèces' };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
            <th className="py-2">Date</th>
            <th className="py-2">Mode</th>
            <th className="py-2">Référence</th>
            <th className="py-2 text-right">Montant</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2.5 text-slate-700">{fmtDate(p.date)}</td>
              <td className="py-2.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  p.mode === 'cheque' ? 'bg-amber-100 text-amber-700' :
                  p.mode === 'virement' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>{modeLabel[p.mode] || p.mode}</span>
              </td>
              <td className="py-2.5 font-mono text-xs text-slate-700">{p.reference || '—'}</td>
              <td className="py-2.5 text-right font-bold text-slate-900">{fmtEUR(p.montant)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableContrats({ rows }) {
  if (!rows?.length) return <EmptyMsg icon={FileText} text="Aucun contrat" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
            <th className="py-2">Type</th>
            <th className="py-2">Période</th>
            <th className="py-2 text-right">Montant</th>
            <th className="py-2">Statut</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2.5 text-slate-800 font-semibold">{c.nombre_affiches || ''}× {c.type_panneau || 'Panneau'}</td>
              <td className="py-2.5 text-slate-700 text-xs">{fmtDate(c.date_debut)} → {fmtDate(c.date_fin)}</td>
              <td className="py-2.5 text-right font-bold">{fmtEUR(c.montant)}</td>
              <td className="py-2.5"><StatusBadge statut={c.statut || 'Actif'} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ statut }) {
  const s = (statut || '').toLowerCase();
  if (s.includes('expire')) return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">{statut}</Badge>;
  if (s.includes('terminé') || s.includes('termine')) return <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 border-0">Terminé</Badge>;
  if (s.includes('en cours') || s.includes('actif')) return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">En cours</Badge>;
  if (s.includes('livré') || s.includes('livre')) return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">Livré</Badge>;
  if (s.includes('payé') || s.includes('paye')) return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">Payé</Badge>;
  return <Badge variant="outline">{statut}</Badge>;
}

function EmptyMsg({ icon: Icon, text }) {
  return (
    <div className="py-10 text-center text-slate-400">
      <Icon className="h-10 w-10 mx-auto mb-2 text-slate-300" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Tableaux d'évolution style Suivi (4 mini-cartes avec sparklines)
// ═══════════════════════════════════════════════════════════
function buildMonthlySeriesClient(items, dateKey, valueKey = null) {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('fr-FR', { month: 'short' }),
      count: 0, value: 0,
    });
  }
  (items || []).forEach((item) => {
    const raw = item[dateKey] || item.date || item.created_at || item.date_debut;
    if (!raw) return;
    const dt = new Date(raw);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    const m = months.find(x => x.key === key);
    if (!m) return;
    m.count += 1;
    if (valueKey) m.value += parseFloat(item[valueKey] || 0);
  });
  return months;
}

function computeTrend(series, field = 'count') {
  const total = series.reduce((s, p) => s + (p[field] || 0), 0);
  const last = series[series.length - 1]?.[field] || 0;
  const prev = series[series.length - 2]?.[field] || 0;
  let pct = 0;
  if (prev > 0) pct = Math.round(((last - prev) / prev) * 100);
  else if (last > 0) pct = 100;
  return { total, last, prev, pct };
}

function ClientEvolutionTables({ d }) {
  const stats = [
    { title: 'Évolution des contrats',   color: '#2563eb', series: buildMonthlySeriesClient(d.contrats, 'date_debut'),               field: 'count', unit: '' },
    { title: 'Évolution des paiements',  color: '#059669', series: buildMonthlySeriesClient(d.paiements || [], 'date', 'montant'),    field: 'value', unit: 'FCFA' },
    { title: 'Évolution locations',      color: '#7c3aed', series: buildMonthlySeriesClient(d.locations_panneaux || [], 'date_debut'),field: 'count', unit: '' },
    { title: 'Évolution prestations',    color: '#f59e0b', series: buildMonthlySeriesClient(d.autres_prestations || [], 'date'),      field: 'count', unit: '' },
  ];
  const fmtVal = (n, unit) => unit ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0)} ${unit}` : (n || 0).toString();
  return (
    <Card className="rounded-2xl h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            Statistiques & tendances
          </h3>
          <span className="text-xs text-slate-500">6 derniers mois</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {stats.map((s) => {
            const t = computeTrend(s.series, s.field);
            const positive = t.pct >= 0;
            return (
              <div
                key={s.title}
                className="border border-slate-200 rounded-xl p-3 bg-white shadow-sm hover:shadow-md transition"
                data-testid={`client-evo-${s.title}`}
              >
                <div className="text-xs font-semibold text-slate-600 mb-1 truncate">{s.title}</div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-lg font-black text-slate-900 truncate">{fmtVal(t.total, s.unit)}</span>
                  <span className={`text-[11px] font-bold whitespace-nowrap ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {positive ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />}
                    {' '}{positive ? '+' : ''}{t.pct}%
                  </span>
                </div>
                <div className="h-12 -mx-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={s.series} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`cg-${s.title.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={s.color} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                      <ReTooltip
                        contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }}
                        labelFormatter={(l) => `Mois : ${l}`}
                        formatter={(v) => [fmtVal(v, s.unit), 'Total']}
                      />
                      <Area
                        type="monotone"
                        dataKey={s.field}
                        stroke={s.color}
                        strokeWidth={2}
                        fill={`url(#cg-${s.title.replace(/\s+/g, '')})`}
                        dot={{ r: 2, fill: s.color }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
